// SLOT GEOMETRY — one GmTex slot's raster and input windows (pure of the key), shared by the
// device painter (groundgen.js) and the Build's pre-paint (engine/steps/paint_slot, APP_PREPAINT):
// both read EXACTLY the inputs slotReads lists, so a shipped slot is keyed on what it painted from.
//
//   geomOf(key, W, coarse) -> G      the raster, class / ways / crown / SHD2 windows of slot j_tx_ty
//   slotReads(G, W, M, nodeAt) -> [{id, rel, kind, ch?, px?, j?, tx?, ty?}]   the served files it reads
//   sunOf(azDeg, altDeg), marchOf(sun, cell), strataSum(strata) (the rock field's cell count over a box)
import { SLOT_PX, LIGHT, marchSteps, WAYS_MAX_J } from './groundref.js';
import { haloPx } from './groundways.js';

const roundEven = (v) => { const r = Math.round(v); return Math.abs(v % 1) === 0.5 && r % 2 !== 0 ? r - 1 : r; };
const clamp = (v, lo, hi) => (v < lo ? lo : v > hi ? hi : v);

// the sun's terms (_prepWorld; the gate's geometry)
export function sunOf(azDeg, altDeg) {
  const az = azDeg * Math.PI / 180, alt = altDeg * Math.PI / 180;
  return { sx: Math.sin(az) * Math.cos(alt), sy: Math.cos(az) * Math.cos(alt), sz: Math.sin(alt), tan: Math.tan(alt), dx: Math.sin(az), dy: -Math.cos(az) };
}
// the rock field's (groundref strataField) integral of its strata cells -> sum(i0, k0, i1, k1): the
// strata cells in that box (geomOf's G.str)
export function strataSum(SF) {
  const { w, h, F } = SF, I = new Uint32Array((w + 1) * (h + 1));
  for (let k = 0; k < h; k++) for (let i = 0; i < w; i++) {
    const o = 4 * (k * w + i), on = F[o] || F[o + 1] || F[o + 2] ? 1 : 0;
    I[(k + 1) * (w + 1) + i + 1] = on + I[k * (w + 1) + i + 1] + I[(k + 1) * (w + 1) + i] - I[k * (w + 1) + i];
  }
  return (i0, k0, i1, k1) => I[(k1 + 1) * (w + 1) + i1 + 1] - I[k0 * (w + 1) + i1 + 1] - I[(k1 + 1) * (w + 1) + i0] + I[k0 * (w + 1) + i0];
}
// the march steps per sun + cell (made once: every slot of a level shares them, read only)
const _march = new WeakMap();
export function marchOf(sun, cell) {
  let m = _march.get(sun);
  if (!m) _march.set(sun, (m = new Map()));
  let v = m.get(cell);
  if (!v) m.set(cell, (v = marchSteps({ sun }, cell)));
  return v;
}
// slot key j_tx_ty in a world W {W, H (class cells, 1 m), sun, wayP {W, H} | null, shd2 {vis, sky:
// [{cell, w, h}]}} (coarse: the coarse class grid's stride) -> its raster, class / ways / crown /
// SHD2 windows (GroundGen._geom; the gate plans every level's pieces from it); wayP.q4 (meta.surface.ways.q4): the
// levels past WAYS_MAX_J get the ways' 4 m mass window G.q4 (WAYS-Q4)
export function geomOf(key, W, coarse) {
  const [j, tx, ty] = key.split('_').map(Number);
  const S = 128 * 2 ** j, x0 = tx * S, z0 = ty * S, texel = S / SLOT_PX, m = haloPx(texel), PW = SLOT_PX + 2 * m, hm = m * texel;
  const Wc = W.W, Hc = W.H, fine1 = j <= WAYS_MAX_J;
  const cl = (v, n) => clamp(v, 0, n - 1);
  // the class window: every cell the blend's boxes, the host ring and the light read (clamped into the map)
  const ca = [cl(Math.floor(x0 - hm) - 2, Wc), cl(Math.floor(z0 - hm) - 2, Hc)], cb = [cl(Math.ceil(x0 + S + hm) + 2, Wc), cl(Math.ceil(z0 + S + hm) + 2, Hc)];
  const st = fine1 ? 1 : coarse;
  const cls = { S: st, x0: Math.floor(ca[0] / st), z0: Math.floor(ca[1] / st) };
  cls.w = Math.floor(cb[0] / st) - cls.x0 + 1;
  cls.h = Math.floor(cb[1] / st) - cls.z0 + 1;
  // the in-world texels (apron) on the float32 raster (groundref xs / zs)
  const f32 = Math.fround, inA = [PW, 0, PW, 0];
  for (let k = 0; k < PW; k++) {
    const x = f32(f32(x0) + f32((k - m + 0.5) * f32(texel))), z = f32(f32(z0) + f32((k - m + 0.5) * f32(texel)));
    if (x >= 0 && x < Wc) { inA[0] = Math.min(inA[0], k); inA[1] = Math.max(inA[1], k + 1); }
    if (z >= 0 && z < Hc) { inA[2] = Math.min(inA[2], k); inA[3] = Math.max(inA[3], k + 1); }
  }
  if (inA[1] <= inA[0]) inA[0] = inA[1] = 0;
  if (inA[3] <= inA[2]) inA[2] = inA[3] = 0;
  const allIn = inA[0] === 0 && inA[1] === PW && inA[2] === 0 && inA[3] === PW;
  // the ways field window (1 m class tiles only)
  const WP = W.wayP;
  let ways = null;
  if (fine1 && WP) {
    const W1 = WP.W, H1 = WP.H, a = [cl(Math.floor(x0 - hm) - 6, W1), cl(Math.floor(z0 - hm) - 6, H1)];
    const b = [cl(Math.ceil(x0 + S + hm) + 6, W1), cl(Math.ceil(z0 + S + hm) + 6, H1)];
    ways = { W1, H1, x0: a[0], z0: a[1], w: b[0] - a[0] + 1, h: b[1] - a[1] + 1 };
  }
  // WAYS-Q4: past the 1 m field the ways' 4 m mass (every 4 m cell under the haloed raster + one, clipped)
  let q4 = null;
  const Q = WP && WP.q4;
  if (!fine1 && Q) {
    const QW = Q.QW, QH = Q.QH, c = Q.cell_m, a = [cl(Math.floor((x0 - hm) / c) - 1, QW), cl(Math.floor((z0 - hm) / c) - 1, QH)];
    const b = [cl(Math.ceil((x0 + S + hm) / c) + 1, QW), cl(Math.ceil((z0 + S + hm) / c) + 1, QH)];
    q4 = { QW, QH, x0: a[0], z0: a[1], w: b[0] - a[0] + 1, h: b[1] - a[1] + 1 };
  }
  // the crown grid (groundref crownGrid)
  const cell = texel <= 1.0 ? 0.5 : texel, ms = marchOf(W.sun, cell), halo = Math.ceil(ms.reach / cell) + 3;
  const ia = Math.max(0, Math.floor(x0 / cell) - halo), ib = Math.min(Math.ceil(Wc / cell), Math.ceil((x0 + S) / cell) + halo);
  const ka = Math.max(0, Math.floor(z0 / cell) - halo), kb = Math.min(Math.ceil(Hc / cell), Math.ceil((z0 + S) / cell) + halo);
  const nx = ib - ia, nz = kb - ka;
  const cr = { cell, steps: ms.steps, nx, nz, X0: ia * cell, Z0: ka * cell, on: nx > 0 && nz > 0 };
  if (cr.on) {
    cr.ja = Math.max(0, Math.floor(x0 / cell) - ia - 3); cr.jb = Math.min(nx, Math.ceil((x0 + S) / cell) - ia + 3);
    cr.la = Math.max(0, Math.floor(z0 / cell) - ka - 3); cr.lb = Math.min(nz, Math.ceil((z0 + S) / cell) - ka + 3);
    cr.box = [cr.ja ? cr.ja + 1 : 0, cr.jb < nx ? cr.jb - 1 : nx, cr.la ? cr.la + 1 : 0, cr.lb < nz ? cr.lb - 1 : nz];
    cr.sr = LIGHT.SKY_FINE_M.map((r) => Math.max(1, roundEven(r / cell)));
  }
  // the SHD2 level + window per channel (groundref shd2Level; the bilinear taps of the slot's texels)
  const shd = {};
  for (const ch of ['vis', 'sky']) {
    const lvls = W.shd2[ch];
    let L = lvls[0];
    for (const l of lvls) if (l.cell <= texel + 1e-9) L = l;
    const i0 = cl(Math.floor((x0 + texel / 2) / L.cell - 0.5), L.w), i1 = cl(Math.floor((x0 + S - texel / 2) / L.cell - 0.5) + 1, L.w);
    const k0 = cl(Math.floor((z0 + texel / 2) / L.cell - 0.5), L.h), k1 = cl(Math.floor((z0 + S - texel / 2) / L.cell - 0.5) + 1, L.h);
    shd[ch] = { L, x0: i0, z0: k0, w: i1 - i0 + 1, h: k1 - k0 + 1 };
  }
  const G = { key, j, tx, ty, S, x0, z0, texel, m, PW, hm, fine1, cls, inA, allIn, ways, q4, cr, shd,
    r: Math.max(1, roundEven(2.0 / texel)), ra: Math.max(1, roundEven(6.0 / texel)) };
  // the node / pack span: the raster, the crown grid, the crowns' reach (16 m)
  const xa = Math.min(x0 - hm - 1, cr.on ? cr.X0 - 17 : Infinity), xb = Math.max(x0 + S + hm + 1, cr.on ? cr.X0 + nx * cell + 17 : -Infinity);
  const za = Math.min(z0 - hm - 1, cr.on ? cr.Z0 - 17 : Infinity), zb = Math.max(z0 + S + hm + 1, cr.on ? cr.Z0 + nz * cell + 17 : -Infinity);
  G.span = [xa, xb, za, zb];
  // the rock field (gs-family-rock): whether a strata cell lies within a cell of the slot (its light pass's tilt)
  const SF = W.strata;
  G.str = false;
  if (SF) {
    const c = SF.cell, i0 = Math.max(0, Math.floor(x0 / c) - 1), i1 = Math.min(SF.w - 1, Math.floor((x0 + S) / c) + 1);
    const k0 = Math.max(0, Math.floor(z0 / c) - 1), k1 = Math.min(SF.h - 1, Math.floor((z0 + S) / c) + 1);
    if (i1 >= i0 && k1 >= k0) G.str = SF.sum(i0, k0, i1, k1) > 0;
  }
  return G;
}

// the served files slot G reads (GroundGen._inputs; the pre-paint's input selection): ids in a fixed
// order — c: class tiles (1 m levels), w: ways tiles, q: the ways' 4 m mass tiles (levels past WAYS_MAX_J),
// n: ground-tree nodes of level j (nodeAt(k) true),
// t: / s: crown / shrub packs (crIx: the family's tile set, i * 65536 + j), v: SHD2 tiles of the picked
// level. M = meta (surface.tiles, surface.ways, crowns); W = geomOf's world (wayP, shd2 {dir, ext, px})
export function slotReads(G, W, M, nodeAt, crIx = (D) => new Set(D.list.map(([i, jj]) => i * 65536 + jj))) {
  const out = [], s = M.surface, T = s.tiles;
  if (G.fine1) {
    const c = G.cls, tm = T.tile_m;
    for (let ty = Math.floor(c.z0 / tm); ty <= Math.floor((c.z0 + c.h - 1) / tm); ty++) for (let tx = Math.floor(c.x0 / tm); tx <= Math.floor((c.x0 + c.w - 1) / tm); tx++) {
      out.push({ id: `c:${tx}_${ty}`, kind: 'img', ch: 1, rel: `${T.dir}/${tx}_${ty}.${T.ext}`, px: tm });
    }
  }
  if (G.ways) {
    const w = G.ways, P = W.wayP, tm = P.tile_m;
    for (let ty = Math.floor(w.z0 / tm); ty <= Math.floor((w.z0 + w.h - 1) / tm); ty++) for (let tx = Math.floor(w.x0 / tm); tx <= Math.floor((w.x0 + w.w - 1) / tm); tx++) {
      out.push({ id: `w:${tx}_${ty}`, kind: 'img', ch: 8, rel: `${P.dir}/${tx}_${ty}.${P.ext}`, px: tm });
    }
  }
  if (G.q4) {
    const w = G.q4, Q = W.wayP.q4, n = Q.px;
    for (let ty = Math.floor(w.z0 / n); ty <= Math.floor((w.z0 + w.h - 1) / n); ty++) for (let tx = Math.floor(w.x0 / n); tx <= Math.floor((w.x0 + w.w - 1) / n); tx++) {
      out.push({ id: `q:${tx}_${ty}`, kind: 'img', ch: 4, rel: `${Q.dir}/${tx}_${ty}.${Q.ext}`, px: n, q4: true });
    }
  }
  const [xa, xb, za, zb] = G.span, S = G.S;
  for (let ty = Math.floor(za / S); ty <= Math.floor(zb / S); ty++) for (let tx = Math.floor(xa / S); tx <= Math.floor(xb / S); tx++) {
    const k = `${G.j}_${tx}_${ty}`;
    if (nodeAt(k)) out.push({ id: `n:${k}`, kind: 'node', rel: `groundm/L${G.j}/${tx}_${ty}.bin`, j: G.j, tx, ty });
  }
  const C = M.crowns;
  if (G.cr.on && C) {
    for (const [kind, D] of [['t', C], ['s', C.shrubs]]) {
      if (!D || !D.list) continue;
      // the family's tiles as a set (made once a world: a key walks its span's tiles, never the whole list)
      const tm = D.tile_m, I = crIx(D);
      for (let jj = Math.max(0, Math.ceil(za / tm) - 1); jj * tm <= zb; jj++) for (let i = Math.max(0, Math.ceil(xa / tm) - 1); i * tm <= xb; i++) {
        if ((i + 1) * tm < xa || (jj + 1) * tm < za || !I.has(i * 65536 + jj)) continue;
        out.push({ id: `${kind}:${i}_${jj}`, kind: 'cpk', rel: `${D.dir}/${i}_${jj}.cpk` });
      }
    }
  }
  const H = W.shd2;
  for (const ch of ['vis', 'sky']) {
    const w = G.shd[ch], px = H.px, c = String(w.L.cell);
    for (let gz = Math.floor(w.z0 / px); gz <= Math.floor((w.z0 + w.h - 1) / px); gz++) for (let gx = Math.floor(w.x0 / px); gx <= Math.floor((w.x0 + w.w - 1) / px); gx++) {
      out.push({ id: `v:${ch}:${c}:${gx}_${gz}`, kind: 'img', ch: 1, rel: `${H.dir}/${ch}/${c}/${gx}_${gz}.${H.ext}` });
    }
  }
  return out;
}
